import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CreateListingWizard.css';

/**
 * FSBO Listing Wizard - Complete Flow
 * 
 * Steps: State → Property → Listing → Photos → Payment → DocuSign
 * 
 * DocuSign sends:
 * - MLS Flat Fee Listing Agreement
 * - State Seller Disclosure Form
 * - Federal Lead Paint Disclosure (if year built < 1978)
 * 
 * Supports 5 launch states: TX, CO, NC, OH, PA
 */

// ============================================
// CONSTANTS
// ============================================

const STEPS = {
  STATE: 1,
  PROPERTY: 2,
  LISTING: 3,
  PHOTOS: 4,
  PAYMENT: 5,
  DOCUSIGN: 6
};

const STEP_LABELS = {
  [STEPS.STATE]: 'State',
  [STEPS.PROPERTY]: 'Property',
  [STEPS.LISTING]: 'Listing',
  [STEPS.PHOTOS]: 'Photos',
  [STEPS.PAYMENT]: 'Payment',
  [STEPS.DOCUSIGN]: 'Sign'
};

const STATES = [
  { code: 'TX', name: 'Texas' },
  { code: 'CO', name: 'Colorado' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'OH', name: 'Ohio' },
  { code: 'PA', name: 'Pennsylvania' },
];

const PROPERTY_TYPES = [
  { id: 'single_family', name: 'Single Family Home' },
  { id: 'condo', name: 'Condo/Townhouse' },
  { id: 'multi_family', name: 'Multi-Family (2-4 units)' },
  { id: 'land', name: 'Vacant Land' },
];

// State disclosure form info
const STATE_DISCLOSURES = {
  TX: { formId: 'TREC 55-0', formName: "Seller's Disclosure Notice" },
  CO: { formId: 'SPD19', formName: "Seller's Property Disclosure" },
  NC: { formId: 'REC 4.22 + 4.25', formName: "Residential Property Disclosure + Mineral Rights" },
  OH: { formId: 'RESD', formName: "Residential Property Disclosure Form" },
  PA: { formId: 'PAR SPD', formName: "Seller's Property Disclosure Statement" },
};

// ============================================
// MAIN COMPONENT
// ============================================

function CreateListingWizard({ initialData, isEditing, existingDraftId, onClose, onSubmit }) {
  
  // ----------------------------------------
  // STATE
  // ----------------------------------------
  
  const [step, setStep] = useState(null); // Start as null until we know the correct step
  const [listingId, setListingId] = useState(existingDraftId || null);
  const [loading, setLoading] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [error, setError] = useState(null);
  
  // Step 1: State
  const [selectedState, setSelectedState] = useState(initialData?.state || null);
  
  // Step 2: Property
  const [property, setProperty] = useState({
    street: initialData?.street || '',
    city: initialData?.city || '',
    zipCode: initialData?.zipCode || '',
    county: initialData?.county || '',
    yearBuilt: initialData?.yearBuilt || '',
    propertyType: initialData?.propertyType || 'single_family',
    bedrooms: initialData?.bedrooms || '',
    bathrooms: initialData?.bathrooms || '',
    sqft: initialData?.sqft || '',
    lotSize: initialData?.lotSize || '',
    lotSizeUnit: initialData?.lotSizeUnit || 'acres',
  });
  
  // Step 3: Listing
  const [listing, setListing] = useState({
    listingPrice: initialData?.listingPrice || '',
    buyerAgentCommission: initialData?.buyerAgentCommission || '2.5',
    description: initialData?.description || '',
    contactName: initialData?.contactName || '',
    contactPhone: initialData?.contactPhone || '',
    contactEmail: initialData?.contactEmail || '',
  });
  
  // Step 4: Photos
  const [photos, setPhotos] = useState(initialData?.photos || []);
  
  // Step 5: Payment
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, completed
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Step 6: DocuSign
  const [docusignStatus, setDocusignStatus] = useState('pending'); // pending, sent, completed
  const [envelopeId, setEnvelopeId] = useState(null);
  
  // ----------------------------------------
  // LOAD DRAFT ON MOUNT
  // ----------------------------------------
  
  useEffect(() => {
    const loadDraft = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setStep(STEPS.STATE);
          setLoadingDraft(false);
          return;
        }
        
        // Fetch user's draft listings
        const response = await fetch('/api/listings/my-drafts', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          setStep(STEPS.STATE);
          setLoadingDraft(false);
          return;
        }
        
        const data = await response.json();
        
        // If there's a draft, restore it
        if (data.drafts && data.drafts.length > 0) {
          const draft = data.drafts[0]; // Get most recent draft
          setListingId(draft.id);
          
          // Restore state
          if (draft.state) setSelectedState(draft.state);
          
          // Restore property details
          setProperty({
            street: draft.address || '',
            city: draft.city || '',
            zipCode: draft.zip_code || '',
            county: draft.county || '',
            yearBuilt: draft.year_built ? String(draft.year_built) : '',
            propertyType: draft.property_type || 'single_family',
            bedrooms: draft.bedrooms ? String(draft.bedrooms) : '',
            bathrooms: draft.bathrooms ? String(draft.bathrooms) : '',
            sqft: draft.square_feet ? String(draft.square_feet) : '',
            lotSize: draft.lot_size ? String(draft.lot_size) : '',
          });
          
          // Restore listing details
          setListing({
            listingPrice: draft.price ? String(draft.price) : '',
            buyerAgentCommission: draft.buyer_agent_commission ? String(draft.buyer_agent_commission) : '2.5',
            description: draft.description || '',
            contactName: draft.contact_name || '',
            contactPhone: draft.contact_phone || '',
            contactEmail: draft.contact_email || '',
          });
          
          // Load photos if they exist
          if (draft.images && draft.images.length > 0) {
            const loadedPhotos = draft.images.map(img => ({
              id: img.id,
              image_url: img.image_url,
              preview: img.image_url,
              name: img.image_url.split('/').pop()
            }));
            setPhotos(loadedPhotos);
          }
          
          // Determine which step to resume from based on filled data
          let resumeStep = STEPS.STATE;
          if (draft.state) resumeStep = STEPS.PROPERTY;
          if (draft.address && draft.city && draft.bedrooms) resumeStep = STEPS.LISTING;
          if (draft.price && draft.contact_name) resumeStep = STEPS.PHOTOS;
          if (draft.images && draft.images.length > 0) resumeStep = STEPS.PAYMENT;
          
          // If payment is already complete, go directly to DocuSign
          if (draft.payment_status === 'paid') {
            resumeStep = STEPS.DOCUSIGN;
            setPaymentStatus('completed');
          }
          
          setStep(resumeStep);
        } else {
          // No draft found, start from beginning
          setStep(STEPS.STATE);
        }
      } catch (err) {
        console.error('Error loading draft:', err);
        setStep(STEPS.STATE);
      } finally {
        setLoadingDraft(false);
      }
    };
    
    loadDraft();
  }, []);
  
  // ----------------------------------------
  // AUTO-SAVE ON DATA CHANGE (debounced)
  // ----------------------------------------
  
  const saveTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);
  
  const debouncedSave = useCallback(() => {
    // Skip save during initial load
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout for 1 second after last change
    saveTimeoutRef.current = setTimeout(async () => {
      // Only save if we have enough data
      if (selectedState && (property.street || property.city)) {
        await saveDraft();
      }
    }, 1000);
  }, [selectedState, property, listing]);
  
  // Trigger auto-save when property or listing data changes
  useEffect(() => {
    debouncedSave();
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [selectedState, property, listing, debouncedSave]);
  
  // ----------------------------------------
  // COMPUTED VALUES
  // ----------------------------------------
  
  const stateName = STATES.find(s => s.code === selectedState)?.name || '';
  const needsLeadPaint = property.yearBuilt && parseInt(property.yearBuilt) < 1978;
  const stateDisclosure = STATE_DISCLOSURES[selectedState];
  const totalSteps = 6;
  const progressPercent = (step / totalSteps) * 100;
  
  // ----------------------------------------
  // VALIDATION
  // ----------------------------------------
  
  const canProceedFromState = () => selectedState !== null;
  
  const canProceedFromProperty = () => {
    return (
      property.street.trim() &&
      property.city.trim() &&
      property.zipCode.trim() &&
      property.yearBuilt &&
      property.propertyType &&
      property.bedrooms &&
      property.bathrooms &&
      property.sqft
    );
  };
  
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canProceedFromListing = () => {
    return (
      listing.listingPrice &&
      listing.buyerAgentCommission &&
      listing.contactName.trim() &&
      listing.contactPhone.trim() &&
      listing.contactEmail.trim() &&
      isValidEmail(listing.contactEmail.trim())
    );
  };
  
  const canProceedFromPhotos = () => {
    return photos.length >= 1; // At least 1 photo required
  };
  
  const canProceedFromPayment = () => {
    return paymentStatus === 'completed';
  };
  
  const canProceedFromDocusign = () => {
    return docusignStatus === 'completed';
  };
  
  // ----------------------------------------
  // API CALLS
  // ----------------------------------------
  
  const saveDraft = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Use field names that match the backend
      const payload = {
        state: selectedState,
        address: property.street,
        city: property.city,
        zipCode: property.zipCode,
        county: property.county,
        yearBuilt: property.yearBuilt,
        propertyType: property.propertyType,
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        squareFeet: property.sqft,
        lotSize: property.lotSizeUnit === 'sqft' && property.lotSize
          ? (parseFloat(property.lotSize) / 43560).toFixed(4)
          : property.lotSize,
        price: listing.listingPrice,
        buyerAgentCommission: listing.buyerAgentCommission,
        description: listing.description,
        contactName: listing.contactName,
        contactPhone: listing.contactPhone,
        contactEmail: listing.contactEmail
      };
      
      if (listingId) {
        // Update existing draft
        const response = await fetch(`/api/listings/${listingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to update listing');
      } else {
        // Create new draft
        const response = await fetch('/api/listings/create-draft', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) throw new Error('Failed to create listing');
        const data = await response.json();
        setListingId(data.listingId || data.id);
      }
    } catch (err) {
      setError('Failed to save. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const processPayment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Collect all listing data for payment
      const listingData = {
        listingId,
        draftId: listingId, // Pass the draft ID for Stripe
        state: selectedState,
        property,
        listing,
        photos,
        needsLeadPaint
      };
      
      // Call parent handler for Stripe checkout
      // This will redirect to Stripe, so we don't expect a return
      if (onSubmit) {
        await onSubmit(listingData, photos);
        // If we get here, the redirect didn't happen (e.g., for paid listings being edited)
        // The parent handler will close the wizard in that case
      }
    } catch (err) {
      setError('Failed to process payment. Please try again.');
      console.error(err);
      setLoading(false);
    }
    // Don't setLoading(false) here - we're redirecting to Stripe
  };
  
  const initiateDocusign = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Fetch the latest listing data from server to ensure we have contact info
      const listingResponse = await fetch(`/api/listings/${listingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!listingResponse.ok) throw new Error('Failed to fetch listing data');
      const currentListing = await listingResponse.json();
      
      console.log('Fetched listing for DocuSign:', currentListing);
      
      // Initiate DocuSign with data from server
      const response = await fetch(`/api/docusign/send-envelope`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          listingId: listingId,
          state: currentListing.state || selectedState,
          yearBuilt: currentListing.year_built || property.yearBuilt,
          needsLeadPaint: currentListing.year_built ? parseInt(currentListing.year_built) < 1978 : needsLeadPaint,
          signerName: currentListing.contact_name || listing.contactName,
          signerEmail: currentListing.contact_email || listing.contactEmail,
          property: {
            street: currentListing.address || property.street,
            city: currentListing.city || property.city,
            zipCode: currentListing.zip_code || property.zipCode
          }
        })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to initiate signing');
      }
      const data = await response.json();
      setEnvelopeId(data.envelopeId);
      setDocusignStatus('sent');
      
      // Redirect to DocuSign (same tab)
      if (data.signingUrl) {
        window.location.href = data.signingUrl;
      }
    } catch (err) {
      setError('Failed to initiate document signing. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const finalizeListing = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Mark listing as complete and ready for MLS submission
      const response = await fetch(`/api/listings/${listingId}/finalize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          envelopeId,
          paymentStatus: 'completed',
          docusignStatus: 'completed'
        })
      });
      
      if (!response.ok) throw new Error('Failed to finalize listing');
      
      // Close wizard and show success
      onClose && onClose({ success: true, listingId });
    } catch (err) {
      setError('Failed to finalize listing. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // ----------------------------------------
  // NAVIGATION
  // ----------------------------------------
  
  const goNext = async () => {
    if (step === STEPS.PROPERTY || step === STEPS.LISTING) {
      await saveDraft();
    }
    
    if (step === STEPS.PHOTOS) {
      // Upload photos before moving to payment
      setLoading(true);
      await uploadPhotosToServer();
      setLoading(false);
    }
    
    if (step < STEPS.DOCUSIGN) {
      setStep(step + 1);
    }
  };
  
  const goBack = () => {
    if (step > STEPS.STATE) {
      setStep(step - 1);
    }
  };
  
  // ----------------------------------------
  // PHOTO HANDLING
  // ----------------------------------------
  
  // Upload a single photo immediately when added
  const uploadSinglePhoto = async (photo, index) => {
    if (!listingId) {
      // Need to create draft first
      await saveDraft();
    }
    
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', photo.file);
    formData.append('isPrimary', index === 0 ? 'true' : 'false');
    
    try {
      const response = await fetch(`/api/listings/${listingId}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        return {
          ...photo,
          id: data.image?.id,
          image_url: data.image?.url,
          uploaded: true
        };
      }
    } catch (err) {
      console.error('Error uploading photo:', err);
    }
    return photo;
  };
  
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview photos immediately
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      uploading: true
    }));
    
    const currentLength = photos.length;
    setPhotos([...photos, ...newPhotos]);
    
    // Upload each photo immediately
    for (let i = 0; i < newPhotos.length; i++) {
      const uploadedPhoto = await uploadSinglePhoto(newPhotos[i], currentLength + i);
      
      // Update the photo in state with server data
      setPhotos(prev => {
        const updated = [...prev];
        const idx = currentLength + i;
        if (updated[idx]) {
          updated[idx] = { ...uploadedPhoto, uploading: false };
        }
        return updated;
      });
    }
  };
  
  const removePhoto = async (index) => {
    const photo = photos[index];
    
    // If photo was uploaded to server, delete it there too
    if (photo.id && listingId) {
      const token = localStorage.getItem('token');
      try {
        await fetch(`/api/listings/${listingId}/images/${photo.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error('Error deleting photo:', err);
      }
    }
    
    const newPhotos = [...photos];
    if (newPhotos[index].preview) {
      URL.revokeObjectURL(newPhotos[index].preview);
    }
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };
  
  const movePhoto = (index, direction) => {
    const newPhotos = [...photos];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newPhotos.length) {
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      setPhotos(newPhotos);
    }
  };
  
  // Upload photos to server (kept for backwards compatibility)
  const uploadPhotosToServer = async () => {
    if (!listingId) return;
    
    const token = localStorage.getItem('token');
    
    // Only upload photos that have a file (not already uploaded)
    const photosToUpload = photos.filter(p => p.file && !p.uploaded);
    
    for (let i = 0; i < photosToUpload.length; i++) {
      const photo = photosToUpload[i];
      const formData = new FormData();
      formData.append('image', photo.file);
      formData.append('isPrimary', i === 0 ? 'true' : 'false');
      
      try {
        const response = await fetch(`/api/listings/${listingId}/images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update photo with server URL so it doesn't re-upload
          photo.image_url = data.image?.url;
          photo.id = data.image?.id;
          delete photo.file; // Remove file reference since it's now uploaded
        }
      } catch (err) {
        console.error('Error uploading photo:', err);
      }
    }
  };
  
  // ----------------------------------------
  // RENDER HELPERS
  // ----------------------------------------
  
  const renderStepLabel = (stepNum, label) => {
    const isCompleted = step > stepNum;
    const isCurrent = step === stepNum;
    
    return (
      <span className={`clw-breadcrumb-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'active' : ''}`}>
        {isCompleted && '✓ '}{label}
      </span>
    );
  };
  
  const updateProperty = (updates) => {
    setProperty(prev => ({ ...prev, ...updates }));
  };
  
  const updateListing = (updates) => {
    setListing(prev => ({ ...prev, ...updates }));
  };
  
  // ----------------------------------------
  // RENDER: STEPS
  // ----------------------------------------
  
  const renderStateStep = () => (
    <div className="clw-step">
      <h1 className="clw-title">Where is your property?</h1>
      <p className="clw-subtitle">Select the state where your property is located.</p>
      
      <div className="clw-state-grid">
        {STATES.map(state => (
          <button
            key={state.code}
            className={`clw-state-btn ${selectedState === state.code ? 'selected' : ''}`}
            onClick={() => setSelectedState(state.code)}
          >
            <span className="clw-state-name">{state.name}</span>
            <span className="clw-state-code">({state.code})</span>
            {selectedState === state.code && <span className="clw-state-check">✓</span>}
          </button>
        ))}
      </div>
      
      <p className="clw-hint">More states coming soon</p>
    </div>
  );
  
  const renderPropertyStep = () => (
    <div className="clw-step">
      <h1 className="clw-title">Property Details</h1>
      <p className="clw-subtitle">Tell us about the property you're selling.</p>
      
      <div className="clw-form">
        <div className="clw-field">
          <label>Street Address <span className="clw-required">*</span></label>
          <input
            type="text"
            value={property.street}
            onChange={e => updateProperty({ street: e.target.value })}
            placeholder="123 Main Street"
          />
        </div>
        
        <div className="clw-row">
          <div className="clw-field">
            <label>City <span className="clw-required">*</span></label>
            <input
              type="text"
              value={property.city}
              onChange={e => updateProperty({ city: e.target.value })}
              placeholder="City"
            />
          </div>
          <div className="clw-field">
            <label>ZIP Code <span className="clw-required">*</span></label>
            <input
              type="text"
              value={property.zipCode}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateProperty({ zipCode: digits });
              }}
              placeholder="12345"
              maxLength={5}
            />
          </div>
        </div>
        
        <div className="clw-row">
          <div className="clw-field">
            <label>County <span className="clw-optional">(optional)</span></label>
            <input
              type="text"
              value={property.county}
              onChange={e => updateProperty({ county: e.target.value })}
              placeholder="County"
            />
          </div>
          <div className="clw-field">
            <label>Year Built <span className="clw-required">*</span></label>
            <input
              type="text"
              value={property.yearBuilt}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                updateProperty({ yearBuilt: digits });
              }}
              placeholder="1985"
              maxLength={4}
            />
            {property.yearBuilt && parseInt(property.yearBuilt) < 1978 && (
              <div style={{ fontSize: '13px', color: '#111827', marginTop: '6px' }}>
                Lead paint disclosure required for homes built before 1978
              </div>
            )}
          </div>
        </div>
        
        <div className="clw-field">
          <label>Property Type <span className="clw-required">*</span></label>
          <select
            value={property.propertyType}
            onChange={e => updateProperty({ propertyType: e.target.value })}
          >
            {PROPERTY_TYPES.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>
        
        <div className="clw-row">
          <div className="clw-field">
            <label>Bedrooms <span className="clw-required">*</span></label>
            <input
              type="number"
              value={property.bedrooms}
              onChange={e => updateProperty({ bedrooms: e.target.value })}
              placeholder="3"
              min="0"
              max="20"
            />
          </div>
          <div className="clw-field">
            <label>Bathrooms <span className="clw-required">*</span></label>
            <input
              type="number"
              value={property.bathrooms}
              onChange={e => updateProperty({ bathrooms: e.target.value })}
              placeholder="2"
              min="0"
              max="20"
              step="0.5"
            />
          </div>
        </div>
        
        <div className="clw-row">
          <div className="clw-field">
            <label>Square Feet <span className="clw-required">*</span></label>
            <input
              type="text"
              value={property.sqft ? parseInt(property.sqft).toLocaleString() : ''}
              onChange={e => {
                const raw = e.target.value.replace(/,/g, '');
                if (raw === '' || /^\d+$/.test(raw)) {
                  updateProperty({ sqft: raw });
                }
              }}
              placeholder="1,500"
            />
          </div>
          <div className="clw-field">
            <label>
              Lot Size ({' '}
              <span
                onClick={() => updateProperty({ lotSizeUnit: 'acres' })}
                style={{
                  cursor: 'pointer',
                  fontWeight: property.lotSizeUnit === 'acres' ? 600 : 400,
                  color: property.lotSizeUnit === 'acres' ? '#111827' : '#9ca3af',
                }}
              >Acres</span>
              <span style={{ color: '#9ca3af', margin: '0 4px' }}>/</span>
              <span
                onClick={() => updateProperty({ lotSizeUnit: 'sqft' })}
                style={{
                  cursor: 'pointer',
                  fontWeight: property.lotSizeUnit === 'sqft' ? 600 : 400,
                  color: property.lotSizeUnit === 'sqft' ? '#111827' : '#9ca3af',
                }}
              >Sq Ft</span>
              {' '})
            </label>
            <input
              type="text"
              value={property.lotSize
                ? (property.lotSizeUnit === 'sqft'
                    ? parseInt(property.lotSize).toLocaleString()
                    : property.lotSize)
                : ''}
              onChange={e => {
                if (property.lotSizeUnit === 'sqft') {
                  const raw = e.target.value.replace(/,/g, '');
                  if (raw === '' || /^\d+$/.test(raw)) {
                    updateProperty({ lotSize: raw });
                  }
                } else {
                  const raw = e.target.value;
                  if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                    updateProperty({ lotSize: raw });
                  }
                }
              }}
              placeholder={property.lotSizeUnit === 'acres' ? '0.25' : '10,890'}
            />
          </div>
        </div>
      </div>
    </div>
  );
  
  const renderListingStep = () => {
    const savings = listing.listingPrice ? (parseInt(listing.listingPrice) * 0.03).toLocaleString() : '0';
    
    return (
    <div className="clw-step">
      <h1 className="clw-title">Listing Details</h1>
      <p className="clw-subtitle">Set your price and contact information.</p>
      
      <div className="clw-form">
        <div className="clw-row">
          <div className="clw-field">
            <label>Listing Price <span className="clw-required">*</span></label>
            <input
              type="text"
              value={listing.listingPrice ? '$' + parseInt(listing.listingPrice).toLocaleString() : ''}
              onChange={e => {
                const raw = e.target.value.replace(/[$,]/g, '');
                if (raw === '' || /^\d+$/.test(raw)) {
                  updateListing({ listingPrice: raw });
                }
              }}
              placeholder="$350,000"
            />
          </div>
          <div className="clw-field">
            <label>Commission % <span className="clw-required">*</span></label>
            <input
              type="number"
              value={listing.buyerAgentCommission}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || /^\d*\.?\d?$/.test(val)) {
                  updateListing({ buyerAgentCommission: val });
                }
              }}
              onBlur={e => {
                let val = parseFloat(e.target.value);
                if (isNaN(val) || val < 2.5) val = 2.5;
                if (val > 3) val = 3;
                updateListing({ buyerAgentCommission: val.toFixed(1) });
              }}
              placeholder="2.5"
              min="2.5"
              max="3"
              step="0.1"
            />
          </div>
        </div>
        
        {listing.listingPrice && (
          <div className="clw-info-card">
            <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
              You are saving ${savings} by not paying a listing agent. We have set the buyer's agent commission to be between 2.5% and 3% to ensure your property gets maximum exposure.
            </p>
          </div>
        )}
        
        <div className="clw-field">
          <label>Property Description <span className="clw-optional">(optional)</span></label>
          <textarea
            value={listing.description}
            onChange={e => updateListing({ description: e.target.value })}
            placeholder="Describe your property's best features..."
            rows={4}
          />
        </div>
        
        <div className="clw-field">
          <label>Contact Name <span className="clw-required">*</span></label>
          <input
            type="text"
            value={listing.contactName}
            onChange={e => updateListing({ contactName: e.target.value })}
            placeholder="Your name"
          />
        </div>
        
        <div className="clw-row">
          <div className="clw-field">
            <label>Contact Phone <span className="clw-required">*</span></label>
            <input
              type="tel"
              value={listing.contactPhone}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                let formatted = '';
                if (digits.length > 0) {
                  formatted = '(' + digits.slice(0, 3);
                  if (digits.length > 3) {
                    formatted += ') ' + digits.slice(3, 6);
                  }
                  if (digits.length > 6) {
                    formatted += '-' + digits.slice(6, 10);
                  }
                }
                updateListing({ contactPhone: formatted });
              }}
              placeholder="(555) 123-4567"
            />
          </div>
          <div className="clw-field">
            <label>Contact Email <span className="clw-required">*</span></label>
            <input
              type="email"
              value={listing.contactEmail}
              onChange={e => updateListing({ contactEmail: e.target.value })}
              placeholder="you@email.com"
              className={listing.contactEmail.trim() && !isValidEmail(listing.contactEmail.trim()) ? 'input-error' : ''}
            />
            {listing.contactEmail.trim() && !isValidEmail(listing.contactEmail.trim()) && (
              <span className="clw-field-error">Please enter a valid email address</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };
  
  const renderPhotosStep = () => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      const remaining = 30 - photos.length;
      const filesToAdd = files.slice(0, remaining);
      const newPhotos = filesToAdd.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name
      }));
      setPhotos([...photos, ...newPhotos]);
    };
    
    return (
      <div className="clw-step">
        <h1 className="clw-title">Property Photos</h1>
        <p className="clw-subtitle">Upload photos of your property. The first photo will be the main listing image.</p>
        
        <div className="clw-photo-upload">
          <label 
            className="clw-upload-area"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                const remaining = 30 - photos.length;
                const filesToAdd = files.slice(0, remaining);
                const newPhotos = filesToAdd.map(file => ({
                  file,
                  preview: URL.createObjectURL(file),
                  name: file.name
                }));
                setPhotos([...photos, ...newPhotos]);
              }}
              style={{ display: 'none' }}
            />
            <div className="clw-upload-content">
              <span className="clw-upload-text">Click to upload photos</span>
              <span className="clw-upload-hint">or drag and drop</span>
            </div>
          </label>
        </div>
        
        {photos.length > 0 && (
          <div className="clw-photo-grid">
            {photos.map((photo, index) => (
              <div key={index} className={`clw-photo-item ${index === 0 ? 'primary' : ''} ${photo.uploading ? 'uploading' : ''}`}>
                <img src={photo.image_url || photo.preview} alt={`Property ${index + 1}`} draggable={false} />
                {index === 0 && <span className="clw-photo-badge">Main Photo</span>}
                {photo.uploading && <span className="clw-photo-uploading">Uploading...</span>}
                <div className="clw-photo-actions">
                  {index > 0 && (
                    <button onClick={() => movePhoto(index, -1)} title="Move left">←</button>
                  )}
                  {index < photos.length - 1 && (
                    <button onClick={() => movePhoto(index, 1)} title="Move right">→</button>
                  )}
                  <button onClick={() => removePhoto(index)} title="Remove">×</button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <p className="clw-hint">
          {photos.length === 0 
            ? 'At least 1 photo required (max 30)' 
            : `${photos.length}/30 photo${photos.length !== 1 ? 's' : ''} uploaded`}
        </p>
      </div>
    );
  };
  
  const renderPaymentStep = () => (
    <div className="clw-step clw-step-centered">
      <h1 className="clw-title">Review & Pay</h1>
      <p className="clw-subtitle">Review your listing details and complete payment</p>
      
      <div className="clw-card">
        <h3>Listing Summary</h3>
        <div className="clw-card-row">
          <span>Property</span>
          <span>{property.street}, {property.city}, {selectedState} {property.zipCode}</span>
        </div>
        <div className="clw-card-row">
          <span>List Price</span>
          <span>${parseInt(listing.listingPrice).toLocaleString()}</span>
        </div>
        <div className="clw-card-row">
          <span>Details</span>
          <span>{property.bedrooms} bed, {property.bathrooms} bath, {parseInt(property.sqft).toLocaleString()} sqft</span>
        </div>
        <div className="clw-card-row">
          <span>Photos</span>
          <span>{photos.length} uploaded</span>
        </div>
      </div>
      
      <div className="clw-card">
        <h3>Payment</h3>
        <div className="clw-card-row" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <span>Flat Fee MLS Listing</span>
          <span>$495.00</span>
        </div>
      </div>
      
      <div className="clw-card">
        <h3>What happens next?</h3>
        <p className="clw-card-text">After payment, you'll complete your disclosure documents via DocuSign. Once signed, your listing will be submitted to the MLS within 24-48 hours.</p>
      </div>
      
      <div className="clw-card">
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
            I have read and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
              Terms of Service
            </a>
            {' '}and authorize Liber Astro to submit my listing to the MLS on my behalf.
          </span>
        </label>
      </div>
      
      {error && <div className="clw-error">{error}</div>}
    </div>
  );
  
  const renderDocusignStep = () => (
    <div className="clw-step clw-step-centered">
      <h1 className="clw-title">Sign Documents</h1>
      <p className="clw-subtitle">Complete your listing documents via DocuSign.</p>
      
      <div className="clw-disclosure-list">
        <div className="clw-disclosure-item">
          <div className="clw-disclosure-number">1</div>
          <div className="clw-disclosure-content">
            <strong>MLS Flat Fee Listing Agreement</strong>
            <p>Authorization to list your property on the MLS</p>
          </div>
        </div>
        
        <div className="clw-disclosure-item">
          <div className="clw-disclosure-number">2</div>
          <div className="clw-disclosure-content">
            <strong>{stateDisclosure?.formName || "Seller's Disclosure"}</strong>
            <p>Required by {stateName} law for residential property sales</p>
            <span className="clw-disclosure-tag">{stateDisclosure?.formId}</span>
          </div>
        </div>
        
        {needsLeadPaint && (
          <div className="clw-disclosure-item">
            <div className="clw-disclosure-number">3</div>
            <div className="clw-disclosure-content">
              <strong>Lead-Based Paint Disclosure</strong>
              <p>Federal requirement for homes built before 1978</p>
              <span className="clw-disclosure-tag">Federal</span>
            </div>
          </div>
        )}
      </div>
      
      {(docusignStatus === 'pending' || docusignStatus === 'sent') && (
        <div className="clw-info-card">
          <strong>Ready to Sign</strong>
          <p>Click the button below to open DocuSign and complete your documents. You'll fill out and sign all forms electronically.</p>
        </div>
      )}
      
      {docusignStatus === 'completed' && (
        <div className="clw-success-card">
          <div className="clw-success-icon">✓</div>
          <strong>Documents Signed</strong>
          <p>All documents have been completed. Your listing will be submitted to the MLS within 24-48 hours.</p>
        </div>
      )}
      
      {error && <div className="clw-error">{error}</div>}
    </div>
  );
  
  // ----------------------------------------
  // RENDER: FOOTER
  // ----------------------------------------
  
  const renderFooter = () => {
    return (
      <div className="clw-footer">
        {step > STEPS.STATE && step !== STEPS.DOCUSIGN && (
          <button className="clw-btn-back" onClick={goBack}>
            Back
          </button>
        )}
        <div className="clw-footer-spacer" />
        
        {step === STEPS.STATE && (
          <button
            className="clw-btn-primary"
            disabled={!canProceedFromState()}
            onClick={goNext}
          >
            Continue
          </button>
        )}
        
        {step === STEPS.PROPERTY && (
          <button
            className="clw-btn-primary"
            disabled={!canProceedFromProperty() || loading}
            onClick={goNext}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        )}
        
        {step === STEPS.LISTING && (
          <button
            className="clw-btn-primary"
            disabled={!canProceedFromListing() || loading}
            onClick={goNext}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        )}
        
        {step === STEPS.PHOTOS && (
          <button
            className="clw-btn-primary"
            disabled={!canProceedFromPhotos()}
            onClick={goNext}
          >
            Continue
          </button>
        )}
        
        {step === STEPS.PAYMENT && (
          <button
            className="clw-btn-primary"
            disabled={loading || !agreedToTerms}
            onClick={processPayment}
          >
            {loading ? 'Processing...' : 'Proceed to Checkout'}
          </button>
        )}
        
        {step === STEPS.DOCUSIGN && (docusignStatus === 'pending' || docusignStatus === 'sent') && (
          <button
            className="clw-btn-primary"
            disabled={loading}
            onClick={initiateDocusign}
          >
            {loading ? 'Opening DocuSign...' : 'Sign Documents'}
          </button>
        )}
        
        {step === STEPS.DOCUSIGN && docusignStatus === 'completed' && (
          <button
            className="clw-btn-primary"
            disabled={loading}
            onClick={finalizeListing}
          >
            {loading ? 'Finalizing...' : 'Complete Listing'}
          </button>
        )}
      </div>
    );
  };
  
  // ----------------------------------------
  // MAIN RENDER
  // ----------------------------------------
  
  // Show loading while checking for drafts or step not yet determined
  if (loadingDraft || step === null) {
    return (
      <div className="clw-overlay">
        <div className="clw-modal">
          <div className="clw-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
            <p style={{ color: '#6b7280', fontSize: '15px' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="clw-overlay" onClick={onClose}>
      <div className="clw-modal" onClick={e => e.stopPropagation()}>
        {/* Progress Bar */}
        <div className="clw-progress">
          <div className="clw-progress-bar" style={{ width: `${progressPercent}%` }} />
        </div>
        
        {/* Header */}
        <div className="clw-header">
          <div className="clw-breadcrumb">
            {Object.entries(STEP_LABELS).map(([stepNum, label], index) => (
              <React.Fragment key={stepNum}>
                {index > 0 && <span className="clw-breadcrumb-sep">›</span>}
                {renderStepLabel(parseInt(stepNum), label)}
              </React.Fragment>
            ))}
          </div>
          <button className="clw-close" onClick={onClose}>×</button>
        </div>
        
        {/* Content */}
        <div className="clw-content">
          {step === STEPS.STATE && renderStateStep()}
          {step === STEPS.PROPERTY && renderPropertyStep()}
          {step === STEPS.LISTING && renderListingStep()}
          {step === STEPS.PHOTOS && renderPhotosStep()}
          {step === STEPS.PAYMENT && renderPaymentStep()}
          {step === STEPS.DOCUSIGN && renderDocusignStep()}
        </div>
        
        {/* Footer */}
        {renderFooter()}
      </div>
    </div>
  );
}

export default CreateListingWizard;
